import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Visibility, ShowChart, Close, ExpandMore } from "@mui/icons-material";
import DropdownField from "../../../components/DropdownField";
import DataTable from "../../../components/DataTable";
import { useForm } from "react-hook-form";
import api from "../../../api";
import CohortExamScoreBarCharts from "../../../components/parent/CohortExamScoreBarCharts";

const defaultFilters = {
  student_id: "",
  course_id: "",
  subject_id: "",
  topic_id: "",
  subtopic_id: "",
  attempt_type: "all",
};

const defaultFilterOptions = {
  students: [],
  courses: [],
  subjects: [],
  topics: [],
  subtopics: [],
  attempt_types: [
    { id: "all", name: "All Attempt" },
    { id: "last", name: "Last Attempt" },
    { id: "first", name: "First Attempt" },
  ],
};

const ALL_OPTION = { id: "", name: "All" };

const AGE_STANDARD_BANDS = [
  { range: "Below 70", label: "Well below average" },
  { range: "70 – 84", label: "Below average" },
  { range: "85 – 94", label: "Low average" },
  { range: "95 – 104", label: "Average" },
  { range: "105 – 114", label: "High average" },
  { range: "115 – 129", label: "Above average" },
  { range: "Above 129", label: "Well above average" },
];

function scoreToChartX(score, innerLeft = 24, innerWidth = 452) {
  const z = (Number(score) - 100) / 15;
  const clamped = Math.max(-3.25, Math.min(3.25, z));
  return innerLeft + ((clamped + 3.25) / 6.5) * innerWidth;
}

function BellCurveChart({ standardisedScore, distribution }) {
  const W = 500;
  const H = 228;
  const baseline = 118;
  const amp = 78;
  const left = 24;
  const innerW = W - 48;
  const sdNumsY = baseline + 12;
  const sdLabelY = baseline + 26;
  const scoreNumsY = baseline + 40;
  const scoreLabelY = baseline + 54;

  let d = "";
  const step = 0.08;
  for (let z = -3.5; z <= 3.5; z += step) {
    const x = left + ((z + 3.5) / 7) * innerW;
    const y = baseline - Math.exp(-0.5 * z * z) * amp;
    d += d === "" ? `M ${x.toFixed(1)} ${y.toFixed(1)}` : ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
  }

  const sdTicks = [-3, -2, -1, 0, 1, 2, 3];
  const scoreTicks = [
    { z: -2, s: 70 },
    { z: -1, s: 85 },
    { z: 0, s: 100 },
    { z: 1, s: 115 },
    { z: 2, s: 130 },
  ];
  const dotX = scoreToChartX(standardisedScore, left, innerW);
  const dotZ = (Number(standardisedScore) - 100) / 15;
  const dotY = baseline - Math.exp(-0.5 * Math.min(3, Math.max(-3, dotZ)) ** 2) * amp;

  const plotTop = 36;
  const plotBottom = baseline;
  const gridStroke = "#cfd8dc";
  const gridDash = "4 5";
  const horizontalGridYs = [48, 64, 80, 96, 112];

  return (
    <Box sx={{ width: "100%", overflow: "visible", aspectRatio: `${W} / ${H}` }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", overflow: "visible" }}
      >
        <text x={W / 2} y={14} textAnchor="middle" fontSize={11} fill="#546e7a">
          x = Mean score
        </text>
        <g aria-hidden="true">
          {horizontalGridYs.map((y) => (
            <line
              key={`h-${y}`}
              x1={left}
              y1={y}
              x2={left + innerW}
              y2={y}
              stroke={gridStroke}
              strokeWidth={0.8}
              strokeDasharray={gridDash}
            />
          ))}
          {sdTicks.map((z) => {
            const gx = left + ((z + 3.5) / 7) * innerW;
            return (
              <line
                key={`v-${z}`}
                x1={gx}
                y1={plotTop}
                x2={gx}
                y2={plotBottom}
                stroke={gridStroke}
                strokeWidth={0.8}
                strokeDasharray={gridDash}
              />
            );
          })}
          <line x1={left} y1={plotBottom} x2={left + innerW} y2={plotBottom} stroke="#90a4ae" strokeWidth={1.2} />
        </g>
        {sdTicks.map((z) => {
          const x = left + ((z + 3.5) / 7) * innerW;
          return (
            <g key={z}>
              <line x1={x} y1={baseline} x2={x} y2={baseline + 6} stroke="#90a4ae" strokeWidth={1} />
              <text x={x} y={sdNumsY} textAnchor="middle" fontSize={10} fill="#455a64" dominantBaseline="middle">
                {z === 0 ? "x" : z}
              </text>
            </g>
          );
        })}
        <text x={W / 2} y={sdLabelY} textAnchor="middle" fontSize={9} fill="#455a64" fontWeight={700}>
          Standard deviation (σ)
        </text>
        <path d={d} fill="none" stroke="#1565c0" strokeWidth={1.6} />
        {scoreTicks.map(({ z, s }) => {
          const x = left + ((z + 3.5) / 7) * innerW;
          return (
            <text
              key={s}
              x={x}
              y={scoreNumsY}
              textAnchor="middle"
              fontSize={10}
              fill="#37474f"
              fontWeight={600}
              dominantBaseline="hanging"
            >
              {s}
            </text>
          );
        })}
        <text x={W / 2} y={scoreLabelY} textAnchor="middle" fontSize={9} fill="#37474f" fontWeight={700}>
          Standardised score
        </text>
        <circle cx={dotX} cy={dotY} r={9} fill="#0d47a1" stroke="#fff" strokeWidth={2} />
        <text
          x={dotX}
          y={dotY + 4}
          textAnchor="middle"
          fontSize={9}
          fill="#fff"
          fontWeight={700}
          style={{ pointerEvents: "none" }}
        >
          {Math.round(Number(standardisedScore))}
        </text>
        {Array.isArray(distribution) &&
          distribution.map((seg) => {
            const zc = Number(seg.z_center);
            if (Number.isNaN(zc)) return null;
            const cx = left + ((zc + 3.5) / 7) * innerW;
            const pct = seg.percent != null ? Number(seg.percent) : 0;
            return (
              <text
                key={seg.key || seg.label}
                x={cx}
                y={54}
                textAnchor="middle"
                fontSize={9}
                fontWeight={700}
                fill="#37474f"
                stroke="#ffffff"
                strokeWidth={3}
                style={{ paintOrder: "stroke fill", pointerEvents: "none" }}
              >
                {pct}%
              </text>
            );
          })}
      </svg>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1, mb: 0.5, px: 0.5, lineHeight: 1.5 }}
      >
        Percentages show the share of students in each σ band (cohort, latest attempt per student). The dot is this
        student&apos;s score.
      </Typography>
    </Box>
  );
}

const TestScores = () => {
  const location = useLocation();
  const { control, setValue } = useForm();
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState(defaultFilterOptions);
  const [results, setResults] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [subtopics, setSubtopics] = useState([]);
  const [subtopicsLoading, setSubtopicsLoading] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState("");
  const [reviewQuestionIndex, setReviewQuestionIndex] = useState(0);
  const [cohortOpen, setCohortOpen] = useState(false);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [cohortData, setCohortData] = useState(null);
  const [cohortError, setCohortError] = useState("");
  const [cohortScoreView, setCohortScoreView] = useState("bar");

  const [formFilters, setFormFilters] = useState(defaultFilters);
  const [filters, setFilters] = useState(defaultFilters);

  const fetchData = async (filterParams) => {
    setLoading(true);
    try {
      const params = {};
      if (filterParams.student_id) params.student_id = filterParams.student_id;
      if (filterParams.course_id) params.course_id = filterParams.course_id;
      if (filterParams.subject_id) params.subject_id = filterParams.subject_id;
      if (filterParams.topic_id) params.topic_id = filterParams.topic_id;
      if (filterParams.subtopic_id) params.subtopic_id = filterParams.subtopic_id;
      if (filterParams.attempt_type) params.attempt_type = filterParams.attempt_type;

      const response = await api.get("parent/test-scores", { params });
      if (response.data?.success && response.data?.data) {
        const { filter_options, results: res } = response.data.data;
        setFilterOptions(filter_options || defaultFilterOptions);
        setResults(res || []);
      } else {
        setFilterOptions(defaultFilterOptions);
        setResults([]);
      }
    } catch (err) {
      console.error("Error fetching test scores:", err);
      setFilterOptions(defaultFilterOptions);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchStudents = async () => {
      setStudentsLoading(true);
      try {
        const response = await api.get("parent/students/names");
        if (response.data?.success && response.data?.data) {
          const list = Array.isArray(response.data.data) ? response.data.data : [];
          const mapped = list.map((s) => ({
            id: String(s.id),
            name: s.full_name || s.email || `Student ${s.id}`,
          }));
          setStudents(mapped);
          if (mapped.length === 1) {
            setFormFilters((prev) => ({ ...prev, student_id: mapped[0].id }));
            setFilters((prev) => ({ ...prev, student_id: mapped[0].id }));
            setValue("student_id", mapped[0].id);
          }
        } else {
          setStudents([]);
        }
      } catch (err) {
        console.error("Error fetching parent students:", err);
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };
    fetchStudents();
  }, [setValue]);

  useEffect(() => {
    if (!formFilters.student_id) {
      setCourses([]);
      setFormFilters((prev) => ({ ...prev, course_id: "", subject_id: "", topic_id: "", subtopic_id: "" }));
      setValue("course_id", "");
      setValue("subject_id", "");
      setValue("topic_id", "");
      setValue("subtopic_id", "");
      setCoursesLoading(false);
      return;
    }
    const fetchStudentCourses = async () => {
      setCoursesLoading(true);
      try {
        const response = await api.get(`parent/student/${formFilters.student_id}/courses`);
        if (response.data?.success && response.data?.data) {
          const list = Array.isArray(response.data.data) ? response.data.data : [];
          setCourses(
            list.map((c) => ({
              id: String(c.id),
              name: c.name,
              subjects: Array.isArray(c.subjects)
                ? c.subjects.map((s) => ({ id: String(s.id), name: s.name }))
                : [],
            }))
          );
        } else {
          setCourses([]);
        }
        setFormFilters((prev) => ({ ...prev, course_id: "", subject_id: "", topic_id: "", subtopic_id: "" }));
        setValue("course_id", "");
        setValue("subject_id", "");
        setValue("topic_id", "");
        setValue("subtopic_id", "");
      } catch (err) {
        console.error("Error fetching student courses:", err);
        setCourses([]);
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchStudentCourses();
  }, [formFilters.student_id, setValue]);

  useEffect(() => {
    if (!formFilters.subject_id) {
      setTopics([]);
      setFormFilters((prev) => ({ ...prev, topic_id: "", subtopic_id: "" }));
      setValue("topic_id", "");
      setValue("subtopic_id", "");
      setTopicsLoading(false);
      return;
    }
    const fetchSubjectTopics = async () => {
      setTopicsLoading(true);
      try {
        const params = new URLSearchParams({ subject_id: formFilters.subject_id });
        if (formFilters.course_id) params.set("course_id", formFilters.course_id);
        const response = await api.get(`parent/subject-topics?${params.toString()}`);
        if (response.data?.success && response.data?.data) {
          const list = Array.isArray(response.data.data) ? response.data.data : [];
          const byName = (arr) => arr.filter((item, i, a) => a.findIndex((x) => x.name === item.name) === i);
          setTopics(byName(list).map((t) => ({ id: String(t.id), name: t.name })));
        } else {
          setTopics([]);
        }
      } catch (err) {
        console.error("Error fetching subject topics:", err);
        setTopics([]);
      } finally {
        setTopicsLoading(false);
      }
    };
    fetchSubjectTopics();
  }, [formFilters.subject_id, formFilters.course_id, setValue]);

  useEffect(() => {
    if (!formFilters.topic_id) {
      setSubtopics([]);
      setFormFilters((prev) => ({ ...prev, subtopic_id: "" }));
      setValue("subtopic_id", "");
      setSubtopicsLoading(false);
      return;
    }
    const fetchTopicSubtopics = async () => {
      setSubtopicsLoading(true);
      try {
        const response = await api.get(`parent/topic-subtopics?topic_id=${formFilters.topic_id}`);
        if (response.data?.success && response.data?.data) {
          const list = Array.isArray(response.data.data) ? response.data.data : [];
          const byName = (arr) => arr.filter((item, i, a) => a.findIndex((x) => x.name === item.name) === i);
          setSubtopics(byName(list).map((s) => ({ id: String(s.id), name: s.name })));
        } else {
          setSubtopics([]);
        }
      } catch (err) {
        console.error("Error fetching topic subtopics:", err);
        setSubtopics([]);
      } finally {
        setSubtopicsLoading(false);
      }
    };
    fetchTopicSubtopics();
  }, [formFilters.topic_id, setValue]);

  useEffect(() => {
    const stateFilters = location.state;
    if (stateFilters && typeof stateFilters === "object") {
      const initialFilters = {
        student_id: stateFilters.student_id || "",
        course_id: stateFilters.course_id || "",
        subject_id: stateFilters.subject_id || "",
        topic_id: stateFilters.topic_id || "",
        subtopic_id: stateFilters.subtopic_id || "",
        attempt_type: stateFilters.attempt_type || "all",
      };
      setFormFilters(initialFilters);
      setFilters(initialFilters);
      Object.entries(initialFilters).forEach(([key, val]) => setValue(key, val || ""));
    }
  }, [location.state]);

  useEffect(() => {
    fetchData(filters);
  }, [filters]);

  const handleFormFilterChange = (key, value) => {
    setFormFilters((prev) => ({ ...prev, [key]: value }));
    setValue(key, value);
    if (key === "student_id") {
      setFormFilters((prev) => ({ ...prev, course_id: "", subject_id: "", topic_id: "", subtopic_id: "" }));
      setValue("course_id", "");
      setValue("subject_id", "");
      setValue("topic_id", "");
      setValue("subtopic_id", "");
    }
    if (key === "course_id") {
      setFormFilters((prev) => ({ ...prev, subject_id: "", topic_id: "", subtopic_id: "" }));
      setValue("subject_id", "");
      setValue("topic_id", "");
      setValue("subtopic_id", "");
    }
    if (key === "subject_id") {
      setFormFilters((prev) => ({ ...prev, topic_id: "", subtopic_id: "" }));
      setValue("topic_id", "");
      setValue("subtopic_id", "");
    }
    if (key === "topic_id") {
      setFormFilters((prev) => ({ ...prev, subtopic_id: "" }));
      setValue("subtopic_id", "");
    }
  };

  const handleSearch = () => {
    setFilters({ ...formFilters });
    setPage(0);
  };

  const subjects = useMemo(() => {
    if (!formFilters.course_id) return [];
    const course = courses.find((c) => String(c.id) === String(formFilters.course_id));
    return course?.subjects ?? [];
  }, [courses, formFilters.course_id]);

  const getAttemptRequestKeys = (row) => {
    if (row?.test_id && row?.student_id != null && row?.attempt_number != null) {
      return {
        test_id: String(row.test_id),
        student_id: String(row.student_id),
        attempt_number: String(row.attempt_number),
      };
    }
    const parts = String(row?.id || "").split("_");
    if (parts.length >= 3) {
      return { test_id: parts[0], student_id: parts[1], attempt_number: parts[2] };
    }
    return null;
  };

  const openAttemptReview = async (row) => {
    const keys = getAttemptRequestKeys(row);
    if (!keys) {
      setViewError("Missing attempt information for this row.");
      setViewData(null);
      setViewOpen(true);
      return;
    }
    setViewError("");
    setViewData(null);
    setReviewQuestionIndex(0);
    setViewOpen(true);
    setViewLoading(true);
    try {
      const q = new URLSearchParams(keys);
      const res = await api.get(`parent/finished-test-attempt?${q.toString()}`);
      if (res.data?.success && res.data?.data) {
        setViewData(res.data.data);
      } else {
        setViewError(res.data?.message || "Could not load attempt details.");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.data?.error ||
        "Could not load attempt details.";
      setViewError(msg);
    } finally {
      setViewLoading(false);
    }
  };

  const closeReviewDialog = () => {
    setViewOpen(false);
    setViewError("");
    setViewData(null);
    setReviewQuestionIndex(0);
  };

  const openCohortDialog = async (row) => {
    const keys = getAttemptRequestKeys(row);
    if (!keys) {
      setCohortError("Missing attempt information for this row.");
      setCohortData(null);
      setCohortScoreView("bar");
      setCohortOpen(true);
      return;
    }
    setCohortError("");
    setCohortData(null);
    setCohortScoreView("bar");
    setCohortOpen(true);
    setCohortLoading(true);
    try {
      const q = new URLSearchParams(keys);
      const res = await api.get(`parent/test-paper-cohort?${q.toString()}`);
      if (res.data?.success && res.data?.data) {
        setCohortData(res.data.data);
      } else {
        setCohortError(res.data?.message || "Could not load cohort statistics.");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.data?.error ||
        "Could not load cohort statistics.";
      setCohortError(msg);
    } finally {
      setCohortLoading(false);
    }
  };

  const closeCohortDialog = () => {
    setCohortOpen(false);
    setCohortError("");
    setCohortData(null);
    setCohortScoreView("bar");
  };

  const incorrectList = viewData?.incorrect_questions;
  const reviewQuestion =
    Array.isArray(incorrectList) && incorrectList.length > 0
      ? incorrectList[Math.min(reviewQuestionIndex, incorrectList.length - 1)]
      : null;
  const reviewTotal = Array.isArray(incorrectList) ? incorrectList.length : 0;
  const reviewHowToSteps =
    reviewQuestion && Array.isArray(reviewQuestion.how_to_find_answer_steps)
      ? reviewQuestion.how_to_find_answer_steps.filter(Boolean)
      : [];

  const columns = [
    { key: "subject", label: "Subject" },
    { key: "topic", label: "Topic" },
    { key: "subtopic", label: "SubTopic" },
    { key: "test_name", label: "Test Name" },
    { key: "attempt_number", label: "Attempt No", render: (row) => {
      const n = row.attempt_number;
      if (n !== undefined && n !== null && !Number.isNaN(Number(n))) return Number(n);
      return "—";
    } },
    { key: "score", label: "Score" },
    { key: "total_questions", label: "Total Q" },
    { key: "total_answered", label: "Total Answered" },
    {
      key: "action",
      label: "Action",
      render: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton
            size="small"
            color="primary"
            aria-label="View attempt review"
            title="View"
            onClick={() => openAttemptReview(row)}
          >
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            aria-label="View cohort statistics"
            title="Cohort"
            onClick={() => openCohortDialog(row)}
          >
            <ShowChart fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const filterFields = [
    {
      name: "student_id",
      label: "Student",
      options: students,
      defaultValue: formFilters.student_id,
      onChange: (val) => handleFormFilterChange("student_id", val),
      loading: studentsLoading,
    },
    {
      name: "course_id",
      label: "Course",
      options: [ALL_OPTION, ...courses],
      defaultValue: formFilters.course_id,
      onChange: (val) => handleFormFilterChange("course_id", val),
      loading: coursesLoading,
    },
    {
      name: "subject_id",
      label: "Subject",
      options: [ALL_OPTION, ...subjects],
      defaultValue: formFilters.subject_id,
      onChange: (val) => handleFormFilterChange("subject_id", val),
    },
    {
      name: "topic_id",
      label: "Topic",
      options: [ALL_OPTION, ...topics],
      defaultValue: formFilters.topic_id,
      onChange: (val) => handleFormFilterChange("topic_id", val),
      loading: topicsLoading,
    },
    {
      name: "subtopic_id",
      label: "SubTopic",
      options: [ALL_OPTION, ...subtopics],
      defaultValue: formFilters.subtopic_id,
      onChange: (val) => handleFormFilterChange("subtopic_id", val),
      loading: subtopicsLoading,
    },
    {
      name: "attempt_type",
      label: "Attempt Type",
      options: filterOptions.attempt_types,
      defaultValue: formFilters.attempt_type,
      onChange: (val) => handleFormFilterChange("attempt_type", val),
    },
  ];

  return (
    <Box p={3}>
      <Typography variant="h5" mb={3}>
        Student Attempt Report
      </Typography>

      <Grid container spacing={2} mb={2}>
        {filterFields.filter((field) => students.length > 1 || field.name !== "student_id").map((field, idx) => (
          <DropdownField
            key={idx}
            control={control}
            name={field.name}
            label={field.label}
            options={field.options}
            defaultValue={field.defaultValue}
            onChange={field.onChange}
            loading={field.loading}
          />
        ))}
        <Grid item xs={12} sm={2} mt="auto">
          <Button
            fullWidth
            variant="contained"
            onClick={handleSearch}
          >
            SEARCH
          </Button>
        </Grid>
      </Grid>

      <DataTable
        loading={loading}
        data={results}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        columns={columns}
        isFilterSelected={true}
      />

      <Dialog
        open={viewOpen}
        onClose={closeReviewDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          component="div"
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pr: 1, pb: 1 }}
        >
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {!viewLoading && !viewError && viewData && viewData.incorrect_count > 0
              ? `Question - Q${reviewQuestionIndex + 1}`
              : "Review attempt"}
          </Typography>
          <IconButton aria-label="close" onClick={closeReviewDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {viewLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!viewLoading && viewError && <Typography color="error">{viewError}</Typography>}
          {!viewLoading && !viewError && viewData && (
            <>
              {viewData.incorrect_count === 0 ? (
                <Typography>
                  There are no incorrect answers for this attempt — every question the student answered was marked
                  correct.
                </Typography>
              ) : (
                reviewQuestion && (
                  <Box>
                    <Paper variant="outlined" sx={{ p: 2.5, mb: 2, bgcolor: "grey.50", borderRadius: 2, borderColor: "divider" }}>
                      <Typography variant="body1" sx={{ mb: reviewQuestion.options?.length ? 2 : 0, lineHeight: 1.6 }}>
                        {reviewQuestion.question_text || "—"}
                      </Typography>
                      {Array.isArray(reviewQuestion.options) && reviewQuestion.options.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center", alignItems: "flex-start" }}>
                          {reviewQuestion.options.map((opt) => (
                            <Box key={opt.option_id ?? opt.letter} sx={{ textAlign: "center", minWidth: 72, flex: "1 1 72px", maxWidth: 160 }}>
                              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 0.5 }}>
                                {opt.letter}
                              </Typography>
                              <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                                {opt.text}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>

                    <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px !important", overflow: "hidden", mb: 1.5, "&:before": { display: "none" } }}>
                      <AccordionSummary expandIcon={<ExpandMore sx={{ color: "common.white", fontSize: 20 }} />} sx={{ bgcolor: "#1565c0", color: "common.white", minHeight: 44, "& .MuiAccordionSummary-content": { my: 1 } }}>
                        <Typography variant="subtitle2" fontWeight={600}>Answer</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ py: 2, bgcolor: "background.paper" }}>
                        <Typography align="center" color="primary" fontWeight={600} variant="body2" sx={{ fontSize: "0.8125rem" }}>
                          Correct Answer: {reviewQuestion.correct_letters?.length ? reviewQuestion.correct_letters.join(", ") : reviewQuestion.correct_answer || "—"}
                        </Typography>
                        <Typography align="center" color="primary" fontWeight={600} variant="body2" sx={{ fontSize: "0.8125rem", mt: 1.25 }}>
                          Chosen Answer: {reviewQuestion.chosen_letter != null && reviewQuestion.chosen_letter !== "" ? reviewQuestion.chosen_letter : reviewQuestion.student_answer && reviewQuestion.student_answer !== "—" ? reviewQuestion.student_answer : "—"}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>

                    <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px !important", overflow: "hidden", "&:before": { display: "none" } }}>
                      <AccordionSummary expandIcon={<ExpandMore sx={{ color: "common.white", fontSize: 20 }} />} sx={{ bgcolor: "#1565c0", color: "common.white", minHeight: 44, "& .MuiAccordionSummary-content": { my: 1 } }}>
                        <Typography variant="subtitle2" fontWeight={600}>Explanation</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ py: 2, bgcolor: "background.paper" }}>
                        {reviewQuestion.explanation && String(reviewQuestion.explanation).trim() ? (
                          <Typography variant="body2" color="primary" sx={{ fontSize: "0.8125rem", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
                            {String(reviewQuestion.explanation).trim()}
                          </Typography>
                        ) : reviewHowToSteps.length > 0 ? (
                          <Box component="ol" sx={{ m: 0, pl: 2.25, color: "primary.main", "& li": { pl: 0.5 } }}>
                            {reviewHowToSteps.map((step, idx) => (
                              <Typography key={idx} component="li" variant="body2" color="primary" sx={{ fontSize: "0.8125rem", lineHeight: 1.65, whiteSpace: "pre-wrap", pb: idx < reviewHowToSteps.length - 1 ? 0.75 : 0 }}>
                                {step}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>—</Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )
              )}
            </>
          )}
        </DialogContent>
        {!viewLoading && !viewError && viewData && viewData.incorrect_count > 0 && (
          <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Button
              variant="contained"
              disabled={reviewQuestionIndex <= 0}
              onClick={() => setReviewQuestionIndex((i) => Math.max(0, i - 1))}
              sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#90a4ae", color: "#fff", "&:hover": { bgcolor: "#78909c" }, "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" } }}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              disabled={reviewQuestionIndex >= reviewTotal - 1}
              onClick={() => setReviewQuestionIndex((i) => Math.min(reviewTotal - 1, i + 1))}
              sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#1565c0", "&:hover": { bgcolor: "#0d47a1" } }}
            >
              Next
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog
        open={cohortOpen}
        onClose={closeCohortDialog}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          component="div"
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, pr: 1, pb: 1, bgcolor: "#1565c0", color: "common.white" }}
        >
          <Stack spacing={1}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
              {cohortScoreView === "bar" ? "Bar chart — same paper" : "Age standardised scoring — same paper"}
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={cohortScoreView}
              onChange={(_, v) => {
                if (v != null) setCohortScoreView(v);
              }}
              size="small"
              color="primary"
              sx={{
                width: "fit-content",
                bgcolor: "rgba(255,255,255,0.16)",
                "& .MuiToggleButton-root": {
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.45)",
                  textTransform: "none",
                  px: 1.5,
                },
                "& .Mui-selected": {
                  bgcolor: "#fff !important",
                  color: "#1565c0 !important",
                },
              }}
            >
              <ToggleButton value="standardised">Standardised scoring</ToggleButton>
              <ToggleButton value="bar">Bar chart</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <IconButton aria-label="close" onClick={closeCohortDialog} size="small" sx={{ color: "common.white" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2, pb: 3, overflow: "visible" }}>
          {cohortLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!cohortLoading && cohortError && <Typography color="error">{cohortError}</Typography>}
          {!cohortLoading && !cohortError && cohortData && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {cohortData.test_name || "Test"} · Cohort: {cohortData.cohort_n ?? 0} student
                {(cohortData.cohort_n ?? 0) === 1 ? "" : "s"} · Mean raw {cohortData.cohort_mean_raw ?? "—"}% · SD{" "}
                {cohortData.cohort_std_raw ?? "—"}
              </Typography>
              {cohortScoreView === "standardised" ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Stack spacing={2}>
                    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                      <Box sx={{ bgcolor: "#1565c0", color: "common.white", px: 2, py: 1 }}>
                        <Typography fontWeight={700} variant="subtitle2">
                          Age standardised scoring
                        </Typography>
                      </Box>
                      <TableContainer sx={{ overflow: "auto" }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "grey.100" }}>
                              <TableCell sx={{ fontWeight: 700 }}>Score range</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {AGE_STANDARD_BANDS.map((row) => (
                              <TableRow key={row.range}>
                                <TableCell>{row.range}</TableCell>
                                <TableCell>{row.label}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        All students — this paper (latest attempt each)
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell align="right" sx={{ width: 64 }}>
                                Rank
                              </TableCell>
                              <TableCell>Student</TableCell>
                              <TableCell align="right">Attempt</TableCell>
                              <TableCell align="right">Raw %</TableCell>
                              <TableCell align="right">Standardised</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(cohortData.cohort_students || []).map((s, idx) => (
                              <TableRow key={`${s.student_id}-${s.attempt_number}`} sx={{ bgcolor: s.is_linked_student ? "action.selected" : "inherit", "& td": { fontWeight: s.is_linked_student ? 700 : 400 } }}>
                                <TableCell align="right">{s.rank ?? idx + 1}</TableCell>
                                <TableCell>{s.student_name}</TableCell>
                                <TableCell align="right">{s.attempt_number}</TableCell>
                                <TableCell align="right">{s.raw_percent}</TableCell>
                                <TableCell align="right">{s.standardised_score}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <Paper variant="outlined" sx={{ flex: 1, p: 1.5, display: "flex", alignItems: "center", gap: 2, borderColor: "grey.400" }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {cohortData.student_name || "Student"}&apos;s standardised score
                        </Typography>
                        <Typography variant="h5" fontWeight={800} color="primary">
                          {cohortData.standardised_score ?? "—"}
                        </Typography>
                      </Paper>
                      <Paper variant="outlined" sx={{ flex: 1, p: 1.5, display: "flex", alignItems: "center", gap: 2, borderColor: "grey.400" }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {cohortData.student_name || "Student"}&apos;s age standardised score
                        </Typography>
                        <Typography variant="h5" fontWeight={800} color="primary">
                          {cohortData.age_standardised_score ?? "—"}
                        </Typography>
                      </Paper>
                    </Stack>
                    <Paper variant="outlined" sx={{ p: 2, overflow: "visible" }}>
                      <BellCurveChart
                        standardisedScore={cohortData.standardised_score ?? 100}
                        distribution={cohortData.cohort_distribution}
                      />
                      {Array.isArray(cohortData.cohort_distribution) && cohortData.cohort_distribution.length > 0 && (
                        <TableContainer sx={{ mt: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: "grey.100" }}>
                                <TableCell sx={{ fontWeight: 700 }}>σ section (cohort)</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                  Students
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                  %
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {cohortData.cohort_distribution.map((row) => (
                                <TableRow key={row.key}>
                                  <TableCell>{row.label}</TableCell>
                                  <TableCell align="right">{row.count}</TableCell>
                                  <TableCell align="right">{row.percent}%</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Paper>
                  </Stack>
                </Grid>
              </Grid>
              ) : (
                <CohortExamScoreBarCharts
                  cohortStudents={cohortData.cohort_students}
                  studentId={cohortData.student_id}
                  studentName={cohortData.student_name}
                  courseName={cohortData.course_name}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={closeCohortDialog} variant="contained" sx={{ bgcolor: "#1565c0" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestScores;
